/**
 * PulseTimeline - Mission Control vertical data timeline
 * 
 * HIGH-CONTRAST HUD — all cards opacity:1, state via color/saturation.
 * Today: bright teal border + glow, white text.
 * Past/Future: solid dark borders (white/15), light grey text (#A0A0A0).
 * Spine: 2px, cyan (past) → amber (future).
 * Cards: solid charcoal bg, strong blur(20px), inner shadow.
 * Rest days: dashed border "ghost" style, fully readable.
 */

import { useEffect, useRef, useMemo } from 'react';
import { format, isToday, isPast, isFuture } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Check,
  Shield,
  Clock,
  Route,
  ChevronRight,
  CalendarClock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RestDayCard } from '@/components/sessions/RestDayCard';
import type { CalendarItem } from '@/types/calendar';
import { formatCompactMinutes } from '@/lib/duration-format';
import { useUnitSystem } from '@/hooks/useUnitSystem';

interface PulseTimelineProps {
  days: Date[];
  getItemsForDay: (date: Date) => CalendarItem[];
  onDayClick?: (date: Date) => void;
  onItemClick?: (item: CalendarItem) => void;
  onReschedule?: (date: Date) => void;
  className?: string;
}

const TEAL = '0, 255, 200';
const AMBER = '255, 180, 60';
const GREY_TEXT = '#A0A0A0';
const WHITE_TEXT = '#E8E8E8';
const CARD_BG_TODAY = 'rgba(14, 22, 32, 0.85)';
const CARD_BG_INACTIVE = 'rgba(12, 16, 24, 0.9)';
const BORDER_INACTIVE = 'rgba(255, 255, 255, 0.12)';

const intentAccent: Record<string, string> = {
  easy: '#34d399',
  steady: '#38bdf8',
  tempo: '#fbbf24',
  intervals: '#f87171',
  long: '#a78bfa',
  rest: '#64748b',
};

const intentBadge: Record<string, { bg: string; text: string; border: string }> = {
  easy:      { bg: 'rgba(52,211,153,0.12)', text: '#34d399', border: 'rgba(52,211,153,0.2)' },
  steady:    { bg: 'rgba(56,189,248,0.12)', text: '#38bdf8', border: 'rgba(56,189,248,0.2)' },
  tempo:     { bg: 'rgba(251,191,36,0.12)', text: '#fbbf24', border: 'rgba(251,191,36,0.2)' },
  intervals: { bg: 'rgba(248,113,113,0.12)', text: '#f87171', border: 'rgba(248,113,113,0.2)' },
  long:      { bg: 'rgba(167,139,250,0.12)', text: '#a78bfa', border: 'rgba(167,139,250,0.2)' },
  rest:      { bg: 'rgba(100,116,139,0.08)', text: '#64748b', border: 'rgba(100,116,139,0.15)' },
};

export function PulseTimeline({
  days,
  getItemsForDay,
  onDayClick,
  onItemClick,
  onReschedule,
  className,
}: PulseTimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);
  const { convertDistance } = useUnitSystem();

  useEffect(() => {
    if (todayRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const todayEl = todayRef.current;
      const offset = todayEl.offsetTop - container.offsetTop - 40;
      container.scrollTo({ top: offset, behavior: 'smooth' });
    }
  }, [days]);

  const weekVolume = useMemo(() => {
    let totalKm = 0;
    let totalMin = 0;
    for (const day of days) {
      for (const item of getItemsForDay(day)) {
        totalMin += item.durationMin;
        if (item.distanceKm) totalKm += item.distanceKm;
      }
    }
    return { totalKm, totalMin };
  }, [days, getItemsForDay]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Volume HUD */}
      <div className="flex-shrink-0 mb-3 mx-1">
        <div
          className="flex items-center justify-between px-3 py-2.5 rounded-xl"
          style={{
            background: CARD_BG_INACTIVE,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${BORDER_INACTIVE}`,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
          }}
        >
          <div className="flex items-center gap-2">
            <Route className="h-3.5 w-3.5" style={{ color: GREY_TEXT }} />
            <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: GREY_TEXT }}>
              Week Volume
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs tabular-nums font-medium" style={{ color: `rgba(${TEAL}, 0.85)` }}>
              {(() => { const d = convertDistance(weekVolume.totalKm); return `${d.value.toFixed(1)} ${d.unit}`; })()}
            </span>
            <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>
            <span className="font-mono text-[10px] tabular-nums" style={{ color: GREY_TEXT }}>
              {formatCompactMinutes(weekVolume.totalMin)}
            </span>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-visible scrollbar-hide scroll-smooth snap-y snap-proximity"
      >
        <div className="relative pl-10 sm:pl-12 pr-1 sm:pr-3 pb-12">

          {days.map((day, idx) => {
            const items = getItemsForDay(day);
            const current = isToday(day);
            const past = isPast(day) && !current;
            const future = isFuture(day);
            const isRestDay = items.length === 0;
            const hasCompleted = items.some((i) => i.kind === 'completed');
            const totalDuration = items.reduce((s, i) => s + i.durationMin, 0);
            const totalDistance = items.reduce((s, i) => s + (i.distanceKm || 0), 0);

            // Spine: solid cyan past, solid amber future
            const spineColor = past || current
              ? `rgba(${TEAL}, 0.5)`
              : `rgba(${AMBER}, 0.3)`;

            // Node styling
            const nodeRing = current
              ? `rgba(${TEAL}, 0.7)`
              : past
                ? `rgba(${TEAL}, 0.3)`
                : `rgba(${AMBER}, 0.2)`;
            const nodeDot = current
              ? `rgba(${TEAL}, 1)`
              : past
                ? `rgba(${TEAL}, 0.5)`
                : `rgba(${AMBER}, 0.35)`;

            // Card styling — NO opacity changes, all cards are opacity:1
            const cardBg = current ? CARD_BG_TODAY : CARD_BG_INACTIVE;
            const cardBorder = current
              ? `rgba(${TEAL}, 0.5)`
              : BORDER_INACTIVE;
            const cardShadow = current
              ? `inset 0 0 40px rgba(${TEAL}, 0.04), 0 0 30px rgba(${TEAL}, 0.08), 0 0 60px rgba(${TEAL}, 0.03)`
              : 'inset 0 1px 0 rgba(255,255,255,0.03), inset 0 0 20px rgba(0,0,0,0.2)';

            // Text colors — white for today, grey for inactive
            const primaryText = current ? WHITE_TEXT : GREY_TEXT;
            const secondaryText = current ? 'rgba(255,255,255,0.55)' : 'rgba(160,160,160,0.6)';
            const tertiaryText = current ? 'rgba(255,255,255,0.4)' : 'rgba(160,160,160,0.45)';

            return (
              <div
                key={format(day, 'yyyy-MM-dd')}
                ref={current ? todayRef : undefined}
                className="relative snap-start"
                style={{ marginBottom: '6px' }}
              >
                {/* Spine segment — 2px solid */}
                {idx < days.length - 1 && (
                  <div
                    className="absolute z-0"
                    style={{
                      left: '-20px',
                      top: '26px',
                      bottom: '-6px',
                      width: '2px',
                      backgroundColor: spineColor,
                    }}
                  />
                )}

                {/* Concentric circle node */}
                <div
                  className="absolute z-10 flex items-center justify-center"
                  style={{ left: '-33px', top: '12px', width: '26px', height: '26px' }}
                >
                  {/* Today outer glow */}
                  {current && (
                    <div
                      className="absolute inset-[-4px] rounded-full"
                      style={{
                        boxShadow: `0 0 14px rgba(${TEAL}, 0.35), 0 0 6px rgba(${TEAL}, 0.25)`,
                      }}
                    />
                  )}
                  {/* Outer ring */}
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      border: `2px solid ${nodeRing}`,
                      background: current ? `rgba(${TEAL}, 0.06)` : 'transparent',
                    }}
                  />
                  {/* Inner dot */}
                  <div
                    className="rounded-full z-10"
                    style={{
                      width: current ? '8px' : '6px',
                      height: current ? '8px' : '6px',
                      backgroundColor: nodeDot,
                    }}
                  />
                </div>

                {/* Card — always opacity:1 */}
                <div
                  className="rounded-xl cursor-pointer transition-colors duration-150"
                  style={{
                    background: cardBg,
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: isRestDay && !current
                      ? `1px dashed rgba(255,255,255,0.08)`
                      : `1px solid ${cardBorder}`,
                    boxShadow: cardShadow,
                  }}
                  onClick={() => onDayClick?.(day)}
                >
                  {/* Day header */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-baseline gap-2">
                      <span
                        className="font-mono text-[11px] uppercase tracking-wider"
                        style={{ color: secondaryText }}
                      >
                        {format(day, 'EEE')}
                      </span>
                      <span
                        className="font-mono text-xl tabular-nums font-bold"
                        style={{ color: current ? `rgba(${TEAL}, 0.95)` : primaryText }}
                      >
                        {format(day, 'd')}
                      </span>
                      <span style={{ color: secondaryText }} className="text-sm">
                        {format(day, 'MMM')}
                      </span>
                      {current && (
                        <span
                          className="font-mono text-[9px] uppercase tracking-[0.15em] ml-1"
                          style={{ color: `rgba(${TEAL}, 0.7)` }}
                        >
                          today
                        </span>
                      )}
                    </div>

                    {isRestDay ? (
                      <span
                        className="font-mono text-[9px] uppercase tracking-wider"
                        style={{ color: secondaryText }}
                      >
                        recovery
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        {totalDistance > 0 && (
                          <span className="font-mono text-[10px] tabular-nums" style={{ color: secondaryText }}>
                            {(() => { const d = convertDistance(totalDistance); return `${d.value.toFixed(1)} ${d.unit}`; })()}
                          </span>
                        )}
                        {totalDuration > 0 && (
                          <span className="font-mono text-[10px] tabular-nums" style={{ color: tertiaryText }}>
                            {formatCompactMinutes(totalDuration)}
                          </span>
                        )}
                        <ChevronRight className="h-3 w-3" style={{ color: tertiaryText }} />
                      </div>
                    )}
                  </div>

                  {/* Sessions */}
                  {items.length > 0 && (
                    <div className="px-4 pb-3 space-y-1">
                      {items.map((item) => {
                        const badge = intentBadge[item.intent] || intentBadge.rest;
                        const accent = intentAccent[item.intent] || intentAccent.rest;
                        return (
                          <div
                            key={item.id}
                            className="flex items-center gap-2.5 py-2 px-3 rounded-lg transition-colors cursor-pointer touch-target"
                            style={{ background: 'transparent' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onItemClick?.(item);
                            }}
                          >
                            {/* Sport dot */}
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: accent }}
                            />

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="text-[13px] font-medium truncate"
                                  style={{ color: primaryText }}
                                >
                                  {item.title}
                                </span>
                                <span
                                  className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0"
                                  style={{
                                    background: badge.bg,
                                    color: badge.text,
                                    border: `1px solid ${badge.border}`,
                                  }}
                                >
                                  {item.intent}
                                </span>
                              </div>
                              <div className="flex items-center gap-2.5 mt-0.5">
                                {item.durationMin > 0 && (
                                  <span
                                    className="font-mono text-[10px] tabular-nums flex items-center gap-0.5"
                                    style={{ color: secondaryText }}
                                  >
                                    <Clock className="h-2.5 w-2.5" />
                                    {formatCompactMinutes(item.durationMin)}
                                  </span>
                                )}
                                {item.distanceKm && item.distanceKm > 0 && (
                                  <span
                                    className="font-mono text-[10px] tabular-nums"
                                    style={{ color: secondaryText }}
                                  >
                                    {(() => { const d = convertDistance(item.distanceKm); return `${d.value.toFixed(1)} ${d.unit}`; })()}
                                  </span>
                                )}
                                {item.pace && (
                                  <span
                                    className="font-mono text-[10px] tabular-nums"
                                    style={{ color: secondaryText }}
                                  >
                                    {item.pace}
                                  </span>
                                )}
                              </div>
                            </div>

                            {item.kind === 'completed' && (
                              <Check className="h-3.5 w-3.5 flex-shrink-0" style={{ color: `rgba(${TEAL}, 0.6)` }} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Future quick actions */}
                  {future && !isRestDay && (
                    <div className="px-4 pb-3 pt-0.5 flex gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-[9px] font-mono uppercase tracking-wider px-2 hover:bg-white/[0.05]"
                        style={{ color: secondaryText }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onReschedule?.(day);
                        }}
                      >
                        <CalendarClock className="h-3 w-3 mr-1" />
                        Reschedule
                      </Button>
                    </div>
                  )}

                  {/* Rest day card */}
                  {isRestDay && (
                    <div className="px-4 pb-4 pt-0.5">
                      <RestDayCard density="standard" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
